import React, {useEffect, useState} from 'react';
import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import styled from 'styled-components';
import Modal from '@src/common/ui/Modal';
import {CustomButton} from '@src/common/inputs/CustomButton';
import {Spinner} from '@src/common/ui/Spinner';
import {useVotingListDetailsQuery} from '@src/queries/voting.queires';
import {VoteAnswerPicker} from '@src/features/voting/components/VoteAnswerPicker';

interface NewVoteModalProps {
  voteId?: string;
  show: boolean;
  onClose: () => void;
}

/** Opens when an organizer starts a vote, so members can answer right away. */
export const NewVoteModal = ({voteId, show, onClose}: NewVoteModalProps) => {
  const {t} = useTranslation();
  const router = useRouter();
  const {data, isLoading} = useVotingListDetailsQuery(voteId ?? '', {
    enabled: show && Boolean(voteId),
  });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    setIsExpired(false);
    if (!data?.expirationDate || !data.isVoteStarted) {
      return;
    }
    const msLeft = new Date(data.expirationDate).getTime() - Date.now();
    if (msLeft <= 0) {
      setIsExpired(true);
      return;
    }
    const timer = setTimeout(() => setIsExpired(true), msLeft);
    return () => clearTimeout(timer);
  }, [data?.expirationDate, data?.isVoteStarted]);

  const isEnded = Boolean(data?.isVoteEnded) || isExpired;
  const canVote = Boolean(data?.isVoteStarted && !isEnded && !data.userAnswer);

  const goToDetails = () => {
    onClose();
    router.push('/voting/voting-info/' + voteId);
  };

  return (
    <Modal
      title={t('common:new-vote-in-your-event')}
      show={show}
      onClose={onClose}
      width="560px"
      height="auto"
    >
      <Content>
        {isLoading || !data ? (
          <Spinner width={32} height={32} />
        ) : (
          <>
            <PollTitle>{data.title}</PollTitle>
            {data.event ? <EventText>{data.event.theme}</EventText> : null}
            {isEnded ? (
              <InfoText>{t('common:vote-modal:ended')}</InfoText>
            ) : data.userAnswer ? (
              <InfoText>{t('common:vote-modal:already-voted')}</InfoText>
            ) : null}
            {!isEnded ? (
              <VoteAnswerPicker
                data={data}
                canVote={canVote}
                onVoted={onClose}
              />
            ) : null}
            <CustomButton
              type="button"
              label={
                isEnded || data.userAnswer
                  ? t('common:vote-modal:results')
                  : t('common:view-details')
              }
              buttonType="secondary"
              onClick={goToDetails}
            />
          </>
        )}
      </Content>
    </Modal>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding-top: 8px;
`;

const PollTitle = styled.p`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
  color: ${(p) => p.theme.colors.primary500};
`;

const EventText = styled.p`
  margin: -8px 0 0;
  font-size: 0.875rem;
  text-align: center;
  color: ${(p) => p.theme.colors.textMuted};
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  text-align: center;
  color: ${(p) => p.theme.colors.textMuted};
`;
